import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { 
  getAllTemplates,
  addTemplateSection,
  updateTemplateSection,
  deleteTemplateSection
} from "@/lib/api";
import { 
  FileText, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Copy,
  Star,
  Save,
  AlertTriangle,
  CheckCircle,
  Loader2,
  BookOpen,
  X
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TemplateBuilderModal } from "@/components/TemplateBuilderModal";

// Simplified Template interface
interface Template {
  id: string;
  title: string;
  description?: string;
  type: 'complaint' | 'medicine' | 'investigation' | 'advice' | 'diagnosis' | 'quick_notes';
  value: any;
  category: 'personal' | 'clinic' | 'shared';
  usage_count: number;
  created_at: string;
  updated_at: string;
  is_favorite: boolean;
  tags: string[];
}

export default function TemplateManagementPage() {
  const { currentUser } = useAuth();

  useEffect(() => { document.title = "Templates | OneCheckUp"; }, []);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderInitial, setBuilderInitial] = useState<any>(null);
  const [builderLoading, setBuilderLoading] = useState(false);
  
  // Template type configurations
  const templateTypes = {
    complaint: { label: 'Complaints', icon: '🗣️' },
    medicine: { label: 'Medicines', icon: '💊' },
    investigation: { label: 'Investigations', icon: '🔬' },
    advice: { label: 'Advice', icon: '📋' },
    diagnosis: { label: 'Diagnosis', icon: '🩺' },
    quick_notes: { label: 'Quick Notes', icon: '📝' }
  };

  // Filtered templates
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      if (activeTab !== 'all' && template.type !== activeTab) return false;
      
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return template.title.toLowerCase().includes(searchLower) ||
               template.description?.toLowerCase().includes(searchLower);
      }
      
      return true;
    });
  }, [templates, activeTab, searchTerm]);

  // Fetch templates
  const fetchTemplates = async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    
    try {
      setLoading(true);
      const data = await getAllTemplates(currentUser.doctor_id, currentUser.clinic_id);
      setTemplates((data as any)?.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      showNotification('error', 'Failed to load templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [currentUser?.doctor_id, currentUser?.clinic_id]);

  // Clear notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
  };

  const openCreateModal = () => {
    setBuilderInitial({
      title: '',
      description: '',
      type: 'medicine',
      category: 'personal',
      tags: [],
      value: []
    });
    setBuilderOpen(true);
  };

  const openEditModal = (template: Template) => {
    let value = template.value;

    if (
        typeof value === "string" &&
        (template.type === "medicine" || template.type === "complaint" || template.type === "investigation")
    ) {
      try {
        value = JSON.parse(value);
      } catch (e) {
        console.warn("[Template Edit] Failed to parse value for template:", template, e);
        value = [];
      }
    }

    if (["advice", "diagnosis", "quick_notes"].includes(template.type)) {
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          value = typeof parsed?.value === "string" ? parsed.value : value;
        } catch {
          value = value;
        }
      } else if (value && typeof value === "object") {
        value = typeof value.value === "string" ? value.value : "";
      } else {
        value = "";
      }
    }

    let tags = Array.isArray(template.tags)
        ? template.tags.filter(t => {
          const isString = typeof t === "string";
          if (!isString) console.warn("[Template Edit] Non-string tag found:", t, template);
          return isString;
        })
      : [];
    setBuilderInitial({
      ...template,
      value,
      tags,
    });
    setBuilderOpen(true);
  };

  const handleBuilderSave = async (data: any) => {
    setBuilderLoading(true);
    try {
      if (builderInitial && builderInitial.id) {
        // Edit
        await updateTemplateSection(builderInitial.id, {
          ...data,
          doctor_id: currentUser.doctor_id,
          clinic_id: currentUser.clinic_id
        });
        showNotification('success', 'Template updated successfully');
      } else {
        // Create
        await addTemplateSection({
          ...data,
          doctor_id: currentUser.doctor_id,
          clinic_id: currentUser.clinic_id
        });
        showNotification('success', 'Template created successfully');
      }
      setBuilderOpen(false);
      setBuilderInitial(null);
      fetchTemplates();
    } catch (error) {
      showNotification('error', 'Failed to save template');
    } finally {
      setBuilderLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string, templateTitle: string) => {
    if (!currentUser?.doctor_id) return;
    if (!confirm(`Are you sure you want to delete "${templateTitle}"?`)) return;
    
    try {
      setLoading(true);
      await deleteTemplateSection(templateId, currentUser.doctor_id);
      showNotification('success', 'Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      showNotification('error', 'Failed to delete template');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateTemplate = async (template: Template) => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    
    try {
      const duplicateData = {
        title: `${template.title} (Copy)`,
        description: template.description,
        type: template.type,
        category: 'personal' as const,
        value: template.value,
        doctor_id: currentUser.doctor_id,
        clinic_id: currentUser.clinic_id,
        tags: []
      };

      await addTemplateSection(duplicateData);
      showNotification('success', 'Template duplicated successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      showNotification('error', 'Failed to duplicate template');
    }
  };

  const closeModals = () => {
    setBuilderOpen(false);
    setBuilderInitial(null);
  };

  const getTypeCount = (type: string) => {
    if (type === 'all') return templates.length;
    return templates.filter(t => t.type === type).length;
  };

  return (
    <div className="prescription-form-container">
      {/* Header */}
      <div className="border-b border-border pb-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-xl">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Template Management</h1>
              <p className="text-sm text-muted-foreground">
                Manage your prescription templates • {templates.length} templates
              </p>
            </div>
          </div>
          
          <Button onClick={openCreateModal} disabled={loading}>
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <Alert className={cn(
          "mb-6 border-l-4",
          notification.type === 'success' 
            ? 'border-success bg-success/10' 
            : 'border-destructive bg-destructive/10'
        )}>
          {notification.type === 'success' ? 
            <CheckCircle className="h-4 w-4 text-success" /> : 
            <AlertTriangle className="h-4 w-4 text-destructive" />
          }
          <AlertDescription className={notification.type === 'success' ? 'text-success' : 'text-destructive'}>
            {notification.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            id="template-search"
            name="template-search"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Template Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7 mb-6">
          <TabsTrigger value="all">
            All ({getTypeCount('all')})
          </TabsTrigger>
          {Object.entries(templateTypes).map(([type, config]) => (
            <TabsTrigger key={type} value={type}>
              {config.label} ({getTypeCount(type)})
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeTab}>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No templates found</h3>
                <p className="text-muted-foreground mb-6">
                  {searchTerm ? 'No templates match your search.' : 'Create your first template to get started.'}
                </p>
                {!searchTerm && (
                  <Button onClick={openCreateModal}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Template
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map(template => (
                <Card key={template.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{templateTypes[template.type].icon}</span>
                        <div>
                          <h3 className="font-medium line-clamp-1">{template.title}</h3>
                          <Badge variant="outline" className="text-xs mt-1">
                            {templateTypes[template.type].label}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    {template.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {template.description}
                      </p>
                    )}
                    
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(template)}
                        className="flex-1"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDuplicateTemplate(template)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteTemplate(template.id, template.title)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Replace old modal with new builder modal */}
      <TemplateBuilderModal
        open={builderOpen}
        onClose={closeModals}
        onSave={handleBuilderSave}
        initialData={builderInitial || {}}
        loading={builderLoading}
      />
    </div>
  );
}